import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    Optional,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import get from 'lodash-es/get';

import { ExxComError } from 'lib/classes/exxcom-error.class';
import { ConfiguratorService } from 'lib/services/configuration/configurator.service';
import { isConfigurationLive } from 'lib/services/configuration/configuration-status.util';
import { CmsPartialComponentClass } from 'lib/components/cms/cms-partial-component.class';
import { CmsService } from 'lib/services/cms.service';
import {
    getMpnFromProductUrl,
    getMpnLookupCandidates,
    resolveConfiguratorMpn,
    resolveDisplayMpn,
} from 'lib/services/webstore-product/product-url.util';
import { calculateReadingTime } from 'lib/tools';
import { WebpService } from 'lib/services/webp.service';
import { CategoryService } from 'lib/services/category/category.service';
import { Location } from '@angular/common';
import { RouterService } from 'lib/services/router.service';
import { cmsMarketoIDsService } from 'lib/services/cmsMarketoIDs.service';
import { getFeatureSolutionCards } from './components/feature-solutions/feature-solutions-row.util';

const scriptName = 'cms-partial-category-top.component';

@Component({
    selector: 'app-cms-partial-category-top',
    templateUrl: './cms-partial-category-top.component.html',
    styleUrls: ['./cms-partial-category-top.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class CmsPartialCategoryTopComponent extends CmsPartialComponentClass implements OnChanges, OnInit, OnDestroy {
    // Properties
    @Input() data: any = {};
    marketoSolutionsID: String;
    private xsBreakpointObserver: Observable<BreakpointState> = this.breakpointObserver.observe(Breakpoints.XSmall);
    private destroy$ = new Subject<void>();
    adblockBackgroundURL: string = 'https://assets.exxactcorp.com/img/exx/cms/ad-blocks/feature-backlink-bg.png';

    activeCategoryName: string = 'Blog';
    recommendedBlogPosts: any = [];
    // Dependencies

    dialog: MatDialog;
    calculateReadingTime: (entry: any) => string;
    getWebpImg: (src: string) => string;
    configuratorService: ConfiguratorService;
    constructor(
        @Inject('environment') environment: any,
        breakpointObserver: BreakpointObserver,
        cmsService: CmsService,
        public cmsMarketoIDsService: cmsMarketoIDsService,
        dialog: MatDialog,
        webpService: WebpService,
        location: Location,
        categoryService: CategoryService,
        configuratorService: ConfiguratorService,
        routerService: RouterService,
        private cdr: ChangeDetectorRef
    ) {
        super({
            dependencies: {
                breakpointObserver,
                dialog,
                configuratorService,
                environment,
                cmsService,
                location,
                categoryService,
                cmsMarketoIDsService,
                routerService,
            },
        });
        this.xsBreakpointObserver.pipe(takeUntil(this.destroy$)).subscribe((size) => {
            if (size.matches) {
                this.adblockBackgroundURL = 'https://assets.exxactcorp.com/img/exx/cms/ad-blocks/feature-backlink-bg-mobile.png';
            } else {
                this.adblockBackgroundURL = 'https://assets.exxactcorp.com/img/exx/cms/ad-blocks/feature-backlink-bg.png';
            }
        });
        this.calculateReadingTime = calculateReadingTime;
        this.getWebpImg = (src: string) => webpService.getWebpImg(src);
    }
    ngOnInit() {
        try {
            this.setFormIDs();
        } catch (err) {
            console.error(...new ExxComError(923241, scriptName, err).stamp());
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    async ngOnChanges(changes: any) {
        try {
            if (!this.data && changes && changes.data && changes.data.currentValue) {
                this.data = changes.data.currentValue;
            }
            const rows = get(changes, 'data.currentValue.rows', []);
            const solutions: any[] = rows
                .filter((r: any) => Object.keys(r)[0] === 'feature_solutions')
                .flatMap((r: any) => getFeatureSolutionCards(r));
            // Every row's MPNs resolve before the first await. The child feature-solutions
            // components share these objects and @defer creates each one as soon as its row is
            // on screen, long before any lookup returns. A child that finds configuratorMpn
            // unset gives up for good, so resolving row by row between lookups cost every
            // visible row after the first its Configure buttons and prices (ST-3811).
            solutions.forEach((solution: any) => this.resolveMpns(solution));
            // One batch across rows, so a later row's price doesn't wait on an earlier row's lookups.
            await Promise.all(solutions.map((solution) => this.configuratorExists(solution)));
            this.cdr.markForCheck(); // Trigger change detection with OnPush after async operations
        } catch (err) {
            console.error(...new ExxComError(999441, scriptName, err).stamp());
        }
    }

    setFormIDs() {
        try {
            this.cmsMarketoIDsService.getFormIDs().then((res) => {
                this.marketoSolutionsID = res?.exx_solution_inquiry_id?.marketo_id;
                // Trigger change detection for OnPush after async data loads
                this.cdr.markForCheck();
            });
        } catch (err) {
            console.error(...new ExxComError(992321, scriptName, err).stamp());
        }
    }

    /**
     * Two MPNs, two jobs. The Solution Page URL and Part Number are authored separately and
     * drift apart, which used to cost a card its Configure button and price because the
     * configuration lookup keyed off the mis-typed value.
     *
     *  - displayMpn is what the card shows and a quote carries: an editor override, else the
     *    URL's MPN, else whatever was authored. Blank means the MPN line is hidden.
     *  - configuratorMpn drives the configuration/price lookups and the Configure href. It has
     *    no authored fallback, so a URL that is not a product page yields no button.
     *
     * Use Custom Part Number overrides both: when it is ticked the URL's MPN is not used at all,
     * so an authored MPN the configurator does not know correctly leaves the card with no
     * Configure button rather than quietly linking somewhere else.
     */
    resolveMpns(solution: any) {
        try {
            const urlMpn = getMpnFromProductUrl(
                get(solution, 'solution_page_link.routerLink', ''),
                this.environment.productUrlRegex,
                this.environment.productUrlBrandPosition
            );
            const authoredMpn = get(solution, 'part_number', '');
            const useCustomMpn = get(solution, 'use_custom_part_number', false);
            solution.displayMpn = resolveDisplayMpn(urlMpn, authoredMpn, useCustomMpn);
            solution.configuratorMpn = resolveConfiguratorMpn(urlMpn, authoredMpn, useCustomMpn);
        } catch (err) {
            console.error(...new ExxComError(999443, scriptName, err).stamp());
        }
    }

    async configuratorExists(solution: any) {
        try {
            // Candidates expand a url variant (TS1-160859871-NVM) to its parent configuration.
            for (const mpn of getMpnLookupCandidates(solution.configuratorMpn)) {
                // The v3-cutover lookup is slow, and a transient null (an aborted
                // or failed request) would silently cost the card its Configure
                // button and price, so retry once.
                let config = await this.configuratorService.getConfigurationWithBasePrice('exx', mpn);
                if (!config) {
                    config = await this.configuratorService.getConfigurationWithBasePrice('exx', mpn);
                }
                if (!config?.[0] || !isConfigurationLive(config[0])) {
                    continue;
                }
                solution.configExists = true;
                solution.price = get(config, '[0].basePrice', get(config, '[0].optimizedConfigurations.good.priceTotal', ''));
                break;
            }
        } catch (err) {
            console.error(...new ExxComError(999442, scriptName, err).stamp());
        }
    }

    get categories() {
        return ['Blog', 'Case Studies', 'Whitepapers', 'eBooks'];
    }

    reformatDate(entry: any) {
        try {
            if (!entry || !entry.date) {
                return '';
            }
            return new Date(entry.date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            });
        } catch (err) {
            console.error(...new ExxComError(484999, scriptName, err).stamp());
        }
    }
}
